import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AuthContext } from "../context/AuthContext";
import { Heart, Tag, MapPin } from "lucide-react";
import purpleMarker from "./marker-icon-2x-violet.png";
import markerShadow from "./marker-shadow.png";
import { translateCategory } from "../utils/categoryTranslations";

//Remove default icon loaders
delete L.Icon.Default.prototype._getIconUrl;
//Add custom marker icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl: purpleMarker,
  iconUrl: purpleMarker,
  shadowUrl: markerShadow,
});

//main component
const ViewDiscount = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, accessToken, language, userRole } = useContext(AuthContext);
  // Get discount ID from state (internal navigation) or query params (new tab)
  const discountId = state?.discountId || searchParams.get('id');

  const [discount, setDiscount] = useState(null);
  const [loading, setLoading] = useState(true);

  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const [isFavorite, setIsFavorite] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(true);
  const [showFavoritePopup, setShowFavoritePopup] = useState(false);

  // Translations object
  const translations = {
    el: {
      viewInfo: "Πληροφορίες προσφοράς",
      product: "Προϊόν",
      category: "Κατηγορία",
      originalPrice: "Αρχική τιμή",
      discountPrice: "Τιμή προσφοράς",
      discountPercent: "Έκπτωση",
      publishedBy: "Δημοσιεύτηκε από",
      city: "Πόλη",
      area: "Περιοχή",
      store: "Κατάστημα",
      avgRate: "Μέση βαθμολογία",
      viewOnMap: "Εμφάνιση στο χάρτη",
      invalidDiscount: "Μη έγκυρη προσφορά.",
      discountNotFound: "Η προσφορά δεν βρέθηκε",
      back: "Επιστροφή",
      loading: "Φόρτωση...",
      favTitle: "Ήδη στα αγαπημένα",
      favMsg: "Αυτή η προσφορά είναι ήδη αποθηκευμένη στη λίστα των αγαπημένων σας.",
      locNotAvail: "Η τοποθεσία δεν είναι διαθέσιμη",
      ok: "Εντάξει",
      addFav: "Προσθήκη στα αγαπημένα",
      alreadyFav: "Ήδη στα αγαπημένα"
    },
    en: {
      viewInfo: "View offer info",
      product: "Product",
      category: "Category",
      originalPrice: "Original price",
      discountPrice: "Offer price",
      discountPercent: "Discount",
      publishedBy: "Published by",
      city: "City",
      area: "Area",
      store: "Store",
      avgRate: "Average rate",
      viewOnMap: "View offer on map",
      invalidDiscount: "Invalid offer.",
      discountNotFound: "Offer not found",
      back: "Back",
      loading: "Loading...",
      favTitle: "Already in favourites",
      favMsg: "This offer is already saved in your favourites list.",
      locNotAvail: "Location not available",
      ok: "OK",
      addFav: "Add to favourites",
      alreadyFav: "Already in favourites"
    }
  };

  const t = translations[language] || translations.el;

  useEffect(() => {
    if (!discountId) return;

    const fetchDiscount = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/viewdiscount/${discountId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );
        const json = await res.json();
        setDiscount(json.discount);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscount();
  }, [discountId, user, accessToken]);

  if (!discountId) {
    return (
      <div className="min-h-screen bg-custom-background">
        <Header />
        <div className="p-10 text-center">
          <p>{t.invalidDiscount}</p>
          <button
            onClick={() => navigate("/discounts")}
            className="mt-4 px-4 py-2 rounded-full bg-custom-grad4 text-white"
          >
            {t.back}
          </button>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!discountId || !user || !accessToken) return;

    const checkFavorite = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/viewdiscount/is-favorite/${discountId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const json = await res.json();
        setIsFavorite(json.isFavorite);
      } catch (err) {
        console.error("Favorite check failed", err);
      } finally {
        setLoadingFavorite(false);
      }
    };

    checkFavorite();
  }, [discountId, user, accessToken]);

  if (loading) return null;

  if (!discount) return <div className="p-10">{t.discountNotFound}</div>;

  const s = discount.full_store;
  const lat = Number(s?.latitude);
  const lng = Number(s?.longitude);
  const hasValidCoords = !isNaN(lat) && !isNaN(lng);

  const getDiscountLangText = (field) => {
    if (!discount) return "—";

    if (field === 'category') {
      // For categories: category_name is English, category_name_el is Greek
      return language === 'el'
        ? discount.category_name_el
        : discount.category_name;
    }

    if (field === 'title') {
      // For title: title contains Greek text, title_en contains English text
      return language === 'el'
        ? discount.title
        : (discount.title_en || discount.title);
    }
    return "—";
  };

  const getLangText = (field) => {
    if (!s) return "—";
    // Construct the database field name by combining field name with language suffix 
    return s[`${field}_${language}`] || s[`${field}_el`] || "—";
  };

  // Submit a rating for this discount
  const submitRating = async (stars) => {
    if (!user || !accessToken) return;
    try {
      setUserRating(stars);

      await fetch(`${process.env.REACT_APP_BACKEND_URL}/viewdiscount/rating`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          discountId,
          stars,
        }),
      });

      // Update UI immediately with new rating (will be confirmed when server responds)
      setDiscount(prev => ({
        ...prev,
        rating: stars, //temporary UI update
      }));

    } catch (err) {
      console.error("Failed to rate", err);
    }
  };

  //if user adds discount to favorites
  const handleAddToFavorites = async () => {
    if (!user || !accessToken) return;

    if (isFavorite) {
      setShowFavoritePopup(true);
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/viewdiscount/add/${discountId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (res.ok) {
        setIsFavorite(true);
      }

    } catch (err) {
      console.error("Failed to add favorite", err);
    }
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />

        <main id="viewdiscount-content" className="flex flex-col flex-1 justify-start px-4 md:px-10 py-10 xl:ml-20 text-left md:text-left">
          <div className="max-w bg-custom-background rounded-2xl">

            <h2 className="text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2">
              <Tag className="text-custom-grad1" /> {t.viewInfo}
            </h2>

            <div className="grid xl:grid-cols-3 gap-6">

              {/* col 1 - info rows */}
              <div className="space-y-3">
                {[
                  [t.product, getDiscountLangText('title')],
                  [t.category, getDiscountLangText('category')],
                  [t.originalPrice, `€${discount.original_price}`],
                  [t.discountPrice, `€${discount.discount_price}`],
                  [t.discountPercent, `${discount.discount}%`],
                  [t.publishedBy, discount.publisher_username || "—"],
                  [t.city, getLangText('city')],
                  [t.area, getLangText('area')],
                  [t.store, getLangText('store_name')],
                  [t.avgRate, `${discount.rating}/5`],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-28 sm:w-32 font-semibold text-custom-letters whitespace-normal text-sm">
                      {label}:
                    </span>
                    <div className="flex-1 min-w-0 bg-custom-header rounded-full px-3 py-2 text-sm text-custom-letters shadow-sm break-words">
                      {value}
                    </div>
                  </div>
                ))}

                {userRole !== 'admin' && (
                  <div className="flex justify-center items-center gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => submitRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="text-3xl transition"
                      >
                        <span className={
                          (hoverRating || userRating || discount.rating) >= star
                            ? "text-custom-grad1"
                            : "text-gray-400"
                        }>
                          ★
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {userRole !== 'admin' && (
                  <>
                    <div className="flex justify-center gap-6 mt-4 items-center">
                      <button
                        onClick={handleAddToFavorites}
                        title={isFavorite ? t.alreadyFav : t.addFav}
                        className={`p-4 rounded-full transition flex items-center justify-center
                          ${isFavorite
                            ? "bg-custom-grad4 text-custom-header"
                            : "bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90"
                          }`}
                      >
                        <Heart
                          size={22}
                          className={isFavorite ? "fill-custom-header" : ""}
                        />
                      </button>
                    </div>
                    {showFavoritePopup && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div role="dialog" aria-modal="true" aria-labelledby="viewdiscount-fav-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl animate-fadeIn text-center">

                          <h3 id="viewdiscount-fav-title" className="text-lg font-semibold text-custom-letters mb-3">
                            {t.favTitle}
                          </h3>

                          <p className="text-sm text-custom-letters mb-6">
                            {t.favMsg}
                          </p>

                          <button
                            onClick={() => setShowFavoritePopup(false)}
                            className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-custom-letters hover:opacity-90 transition"
                          >
                            {t.ok}
                          </button>

                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* col 2 - photo */}
              <div className="flex flex-col items-center justify-start gap-4">
                {/* spacer to match the map title height */}
                <div className="text-xl invisible select-none flex items-center gap-2">
                  <MapPin className="text-custom-grad1" /> {t.viewOnMap}
                </div>
                <div className="w-full max-w-sm aspect-square bg-custom-header rounded-xl flex items-center justify-center shadow">
                  <img
                    src={discount.photo_url || "/placeholder.png"}
                    alt={getDiscountLangText('title')}
                    className="max-h-full max-w-full object-contain rounded-xl"
                  />
                </div>
              </div>

              {/* col 3 - map */}
              <div className="flex flex-col items-center justify-start gap-4">
                <h3 className="text-xl font-semibold text-custom-letters text-center flex justify-center items-center gap-2">
                  <MapPin className="text-custom-grad1" /> {t.viewOnMap}
                </h3>

                <div className="w-full max-w-sm aspect-square rounded-xl overflow-hidden shadow">
                  {hasValidCoords ? (
                    <MapContainer
                      center={[Number(s.latitude), Number(s.longitude)]}
                      zoom={18}
                      className="h-full w-full"
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker position={[Number(s.latitude), Number(s.longitude)]}>
                        <Popup>
                          <div className="min-w-[180px]">
                            <h3 className="font-semibold">{getDiscountLangText('title')}</h3>
                            <p className="text-sm">{getLangText('store_name')}</p>
                          </div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full bg-gray-100 text-sm">
                      {t.locNotAvail}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ViewDiscount;
